import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
  MapPin,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Info,
  Loader2,
  CheckCircle,
  X
} from 'lucide-react';
import usePlacesAutocomplete, {
  getGeocode,
  getZipCode,
  getLatLng,
} from 'use-places-autocomplete';
import { useLoadScript } from '@react-google-maps/api';

const libraries = ['places'];

// Child component that uses usePlacesAutocomplete - only rendered when script is loaded
const LocationSearchInput = ({ formData, updateFormData, validationErrors }) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
    },
    debounce: 300,
  });

  // Sync initial value from formData
  useEffect(() => {
    if (!value && formData.fullAddress) {
      setValue(formData.fullAddress, false);
    }
  }, [formData.fullAddress, setValue, value]);

  const handleSelect = async (address) => {
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);

      const addressComponents = results[0].address_components;
      const getComponent = (type) => addressComponents.find(c => c.types.includes(type))?.long_name || '';
      const getShortComponent = (type) => addressComponents.find(c => c.types.includes(type))?.short_name || '';

      const streetNumber = getComponent('street_number');
      const route = getComponent('route');
      const city = getComponent('locality') || getComponent('postal_town');
      const state = getShortComponent('administrative_area_level_1'); // Use short name for state code often preferred, or long_name if BE expects full Name
      const country = getComponent('country');
      const zipCode = getComponent('postal_code');

      const streetAddress = streetNumber && route ? `${streetNumber} ${route}` : route || address;
      // Prefer Google's geocoded formatted_address — it includes the postcode
      const fullAddress = results[0].formatted_address || address;
      setValue(fullAddress, false);

      console.log("📍 Google Places Result:", {
        address: fullAddress, lat, lng, zipCode, city, state, country, streetAddress
      });

      updateFormData({
        fullAddress: fullAddress,
        street_address: streetAddress,
        zipcode: zipCode || '',
        zipcode_auto_filled: !!zipCode,
        city: city,
        state: state,
        country: country,
        latitude: lat,
        longitude: lng
      });

      // Auto-scroll to budget section
      setTimeout(() => {
        document.getElementById('budget-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);

    } catch (error) {
      console.error('Error selecting place:', error);
    }
  };

  return (
    <div className="space-y-2 relative">
      <Label htmlFor="location" className="text-base font-medium text-gray-900">
        Search Location *
      </Label>
      <div className="relative">
        <Input
          id="location"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!ready}
          placeholder="Enter your address or zip code"
          className="h-12 text-base border-2 focus:border-emerald-500 transition-colors duration-200 pl-12"
          autoComplete="off"
        />
        <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        
        {value && (
          <button
            onClick={() => {
              setValue("", false);
              updateFormData({ zipcode: "", zipcode_auto_filled: false, fullAddress: "", city: "", state: "", apt_unit: "" });
            }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        
        {status === "OK" && (
          <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
            {data.map(({ place_id, description }) => (
              <li
                key={place_id}
                onClick={() => handleSelect(description)}
                className="px-4 py-3 hover:bg-emerald-50 cursor-pointer text-sm text-gray-700 flex items-center space-x-2 border-b border-gray-100 last:border-0"
              >
                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span>{description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const LocationBudgetStep = ({ formData, updateFormData, validationErrors = {}, savedAddresses = [], onSaveAddress }) => {
  const [saveToProfile, setSaveToProfile] = useState(false);
  const [addressLabel, setAddressLabel] = useState('');
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Auto-select the single saved address when step loads
  useEffect(() => {
    if (savedAddresses.length === 1 && !formData.saved_address_id && !formData.zipcode) {
      const addr = savedAddresses[0];
      updateFormData({
        fullAddress: addr.formatted_address || addr.street_address || '',
        street_address: addr.street_address || '',
        city: addr.city || '',
        state: addr.state || '',
        country: addr.country || '',
        zipcode: addr.postcode || '',
        zipcode_auto_filled: !!addr.postcode,
        latitude: addr.latitude || null,
        longitude: addr.longitude || null,
        saved_address_id: addr.id,
        save_address_to_profile: false,
        address_label: '',
      });
    }
  }, [savedAddresses]);

  const budgetRanges = [
    {
      value: 'under_500',
      label: 'Under $500',
      description: 'Small repairs and quick fixes'
    },
    {
      value: '500_1000',
      label: '$500 - $1,000',
      description: 'Small to minor projects'
    },
    {
      value: '1000_5000',
      label: '$1,000 - $5,000',
      description: 'Minor to medium improvements'
    },
    {
      value: '5000_10000',
      label: '$5,000 - $10,000',
      description: 'Medium-sized projects'
    },
    {
      value: '10000_25000',
      label: '$10,000 - $25,000',
      description: 'Major improvements'
    },
    {
      value: 'over_25000',
      label: 'Over $25,000',
      description: 'Large renovations'
    }
  ];

  const handleBudgetSelect = (budgetRange) => {
    updateFormData({
      budget: budgetRange,
      budgetType: 'range'
    });
    
    // Auto-scroll to next step actions
    setTimeout(() => {
      const modalContent = document.querySelector('[data-modal-content="true"]');
      if (modalContent) {
        modalContent.scrollTo({
          top: modalContent.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 150);
  };

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  if (loadError) return <div>Error loading maps</div>;
  
  return (
    <div className="space-y-8 mb-12">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Location & Budget
        </h3>
        <p className="text-gray-600">
          Help us connect you with local service providers and provide accurate quotes
        </p>
      </div>

      {/* Location Section */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <MapPin className="w-5 h-5 text-emerald-600" />
          <h4 className="text-lg font-semibold text-gray-900">Project Location</h4>
        </div>

        {/* Saved address chips */}
        {savedAddresses.length > 0 && (
          <div className="mb-2">
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Use a saved address</Label>
            <div className="flex flex-wrap gap-2">
              {savedAddresses.map(addr => (
                <button
                  key={addr.id}
                  type="button"
                  onClick={() => {
                    updateFormData({
                      fullAddress: addr.formatted_address || addr.street_address || '',
                      street_address: addr.street_address || '',
                      city: addr.city || '',
                      state: addr.state || '',
                      country: addr.country || '',
                      zipcode: addr.postcode || '',
                      zipcode_auto_filled: !!addr.postcode,
                      latitude: addr.latitude || null,
                      longitude: addr.longitude || null,
                      saved_address_id: addr.id,
                      save_address_to_profile: false,
                      address_label: '',
                    });
                    setSaveToProfile(false);
                  }}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    formData.saved_address_id === addr.id
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
                  }`}
                >
                  {addr.label}
                </button>
              ))}
              {formData.saved_address_id && (
                <button
                  type="button"
                  onClick={() => {
                    updateFormData({ fullAddress: '', street_address: '', apt_unit: '', city: '', state: '', country: '', zipcode: '', zipcode_auto_filled: false, latitude: null, longitude: null, saved_address_id: null });
                  }}
                  className="px-3 py-1.5 rounded-full border text-sm bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                >
                  + New address
                </button>
              )}
            </div>
          </div>
        )}

        {/* Conditionally render Search Input only when no saved address selected */}
        {!formData.saved_address_id && (
          isLoaded ? (
            <LocationSearchInput
              formData={formData}
              updateFormData={updateFormData}
              validationErrors={validationErrors}
            />
          ) : (
            <div className="flex justify-center p-4">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          )
        )}

        {/* Zip Code Display - read-only when Google returned one, editable fallback when it didn't */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              {formData.zipcode_auto_filled ? 'Calculated Zip Code' : 'Zip Code *'}
            </Label>
            {formData.zipcode_auto_filled ? (
              <Input
                value={formData.zipcode}
                readOnly
                className="bg-gray-50 border-gray-200"
                placeholder="Auto-filled"
              />
            ) : (
              <>
                <Input
                  value={formData.zipcode || ''}
                  onChange={(e) => handleInputChange('zipcode', e.target.value)}
                  placeholder="e.g. 10001"
                  className={validationErrors.zipcode ? 'border-red-500 focus:border-red-500' : ''}
                />
                {validationErrors.zipcode ? (
                  <p className="text-xs text-red-600">{validationErrors.zipcode}</p>
                ) : (
                  <p className="text-xs text-gray-500">We couldn't auto-detect a zip code. Please enter it manually.</p>
                )}
              </>
            )}
          </div>
          <div className="space-y-2">
             <Label className="text-sm font-medium text-gray-700">City / State</Label>
             <Input 
               value={formData.city && formData.state ? `${formData.city}, ${formData.state}` : ''} 
               readOnly 
               className="bg-gray-50 border-gray-200"
               placeholder="Auto-filled"
             />
          </div>
        </div>

        <p className="text-sm text-gray-500">
          Your specific location helps us find providers near you. We'll only share this with pros you choose to connect with.
        </p>
        
        {/* Apt / Unit / Door # */}
        <div className="space-y-2">
          <Label htmlFor="aptUnit" className="text-base font-medium text-gray-900">
            Apt / Unit / Door # (Optional)
          </Label>
          <Input
            id="aptUnit"
            placeholder="e.g. Apt 4B, Unit 12, Door 3"
            value={formData.apt_unit || ''}
            onChange={(e) => handleInputChange('apt_unit', e.target.value)}
            className="h-12 text-base border-2 focus:border-emerald-500 transition-colors duration-200"
          />
          <p className="text-sm text-gray-500">
            Add a unit, apartment, or door number if applicable.
          </p>
        </div>

        {/* Full Address — read-only preview */}
        {(formData.street_address || formData.city) && (
          <div className="space-y-2">
            <Label className="text-base font-medium text-gray-900">
              Full Address
            </Label>
            <div className="h-auto min-h-12 px-3 py-3 text-base bg-gray-50 border-2 border-gray-200 rounded-md text-gray-700">
              {[
                formData.street_address,
                formData.apt_unit ? `Apt ${formData.apt_unit}` : null,
                [formData.city, formData.state].filter(Boolean).join(', '),
                formData.zipcode,
              ].filter(Boolean).join(', ')}
            </div>
            <p className="text-sm text-gray-500">
              Full address will only be shared with providers you choose to work with.
            </p>
          </div>
        )}

        {/* Save to profile option — only for new addresses when authenticated */}
        {!formData.saved_address_id && onSaveAddress && formData.zipcode && (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="saveAddrToProfile"
                checked={saveToProfile}
                onChange={(e) => {
                  setSaveToProfile(e.target.checked);
                  updateFormData({ save_address_to_profile: e.target.checked, address_label: addressLabel });
                }}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <label htmlFor="saveAddrToProfile" className="text-sm text-gray-700 cursor-pointer">
                Save this address to my profile
              </label>
            </div>
            {saveToProfile && (
              <input
                type="text"
                placeholder="Label (e.g. Home)"
                value={addressLabel}
                onChange={(e) => {
                  setAddressLabel(e.target.value);
                  updateFormData({ address_label: e.target.value });
                }}
                className="border border-emerald-200 rounded px-2 py-1 text-sm w-32 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            )}
          </div>
        )}
      </div>

      {/* Budget Section */}
      <div id="budget-section" className="space-y-6">
        <div className="flex items-center space-x-2">
          <DollarSign className="w-5 h-5 text-emerald-600" />
          <h4 className="text-lg font-semibold text-gray-900">Budget Range</h4>
        </div>

        {/* Budget Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h5 className="font-semibold text-blue-900 mb-1">
                  Why we ask for budget information
                </h5>
                <p className="text-sm text-blue-800">
                  Budget ranges help service providers give you more accurate quotes and ensure 
                  you only receive bids that match your expectations. Your exact budget won't be shared.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Budget Range Selection */}
        <div className="space-y-2">
          <Label className="text-base font-medium text-gray-900">
            What's your budget for this project? *
          </Label>
          {validationErrors.budget && (
            <p className="text-sm text-red-600 font-medium">{validationErrors.budget}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {budgetRanges.map((budget) => (
              <Card
                key={budget.value}
                className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  formData.budget === budget.value
                    ? 'ring-2 ring-emerald-500 bg-emerald-50 border-emerald-200'
                    : 'hover:border-gray-300'
                }`}
                onClick={() => handleBudgetSelect(budget.value)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {budget.label}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {budget.description}
                      </p>
                    </div>
                    {formData.budget === budget.value && (
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Budget Tips */}
        {formData.budget && (
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-emerald-900 mb-1">
                    Budget Tip
                  </h5>
                  <p className="text-sm text-emerald-800">
                    {(formData.budget === 'under_500' || formData.budget === '500_1000') &&
                      "Small projects often get quick responses. Be specific about what needs to be done to get accurate quotes."
                    }
                    {formData.budget === '1000_5000' &&
                      "This range is perfect for minor to medium improvements. Consider bundling related tasks to get better value."
                    }
                    {formData.budget === '5000_10000' &&
                      "Medium projects allow for quality materials. Discuss material options with providers to optimize your budget."
                    }
                    {formData.budget === '10000_25000' &&
                      "Major improvements in this range often include permits. Ask providers about permit requirements early."
                    }
                    {formData.budget === 'over_25000' &&
                      "Large renovations benefit from detailed planning. Consider getting detailed plans and permits before starting."
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Privacy Notice */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-gray-600 mt-0.5" />
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">
                Privacy & Security
              </h5>
              <p className="text-sm text-gray-600">
                Your location information is used only to connect you with local service providers. 
                Your exact address and budget details are kept private and secure.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};